import React, { useState, useRef, useEffect } from 'react';
import './MP3Preview.css';
import playIcon from '../img/play.png';
import pauseIcon from '../img/pause.png';
import coverArt from '../img/preview/cover.png';
import volumeIcon from '../img/preview/volume.png';
import noSoundIcon from '../img/preview/mute.png';

interface MP3PreviewProps {
  fileName: string;
  filePath: string;
}

const MP3Preview: React.FC<MP3PreviewProps> = ({ fileName, filePath }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  const togglePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!audioRef.current) return;
    const time = parseFloat(e.target.value);
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const toggleMute = () => {
    if (!audioRef.current) return;
    audioRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const formatTime = (seconds: number): string => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="mp3-preview">
      <audio ref={audioRef} src={filePath} />

      {/* Top content area: Album art (left) and Metadata (right) */}
      <div className="mp3-content-area">
        <div className="mp3-album-art">
          <img src={coverArt} alt="Album cover" className="mp3-cover-image" />
        </div>

        <div className="mp3-metadata">
          <div className="mp3-time-display">
            Time: {formatTime(duration)}
          </div>
        </div>
      </div>

      {/* Bottom controls bar */}
      <div className="mp3-controls-bar">
        {/* Play/Pause button */}
        <button
          className="mp3-button mp3-play-pause-button"
          onClick={togglePlayPause}
        >
          <img
            src={isPlaying ? pauseIcon : playIcon}
            alt={isPlaying ? 'Pause' : 'Play'}
            className="mp3-control-icon"
          />
        </button>

        {/* Progress bar with timestamps */}
        <div className="mp3-timeline">
          <span className="mp3-time-label">{formatTime(currentTime)}</span>
          <input
            type="range"
            min="0"
            max={duration || 0}
            value={currentTime}
            onChange={handleSeek}
            className="mp3-slider mp3-seek-slider"
            style={{
              '--progress': `${duration > 0 ? (currentTime / duration) * 100 : 0}%`
            } as React.CSSProperties}
          />
          <span className="mp3-time-label">{formatTime(duration - currentTime)}</span>
        </div>

        {/* Volume toggle */}
        <button
          className="mp3-button mp3-volume-button"
          onClick={toggleMute}
          title={isMuted ? 'Unmute' : 'Mute'}
        >
          <img
            src={isMuted ? noSoundIcon : volumeIcon}
            alt={isMuted ? 'Muted' : 'Volume'}
            className="mp3-control-icon"
          />
        </button>
      </div>
    </div>
  );
};

export default MP3Preview;
